import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, mergeMap, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { LoggerService } from '../../../core/services/logger.service';
import {
  Parameter,
  CreateParameterRequest,
  UpdateParameterRequest,
  ParameterDetail
} from '../models/parameter.interface';

@Injectable({
  providedIn: 'root'
})
export class ParameterService {
  private readonly apiUrl = `${environment.baseUrl.generic}/Parametro`;

  private http = inject(HttpClient);
  private logger = inject(LoggerService);

  createParameter(request: CreateParameterRequest): Observable<Parameter> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Accept: 'application/json'
    });

    return this.http.post<Parameter>(`${this.apiUrl}/Create`, request, { headers });
  }

  getAllParameters(): Observable<Parameter[]> {
    return this.http.get<Parameter[]>(`${this.apiUrl}/Get`);
  }

  getParameterById(id: number): Observable<Parameter> {
    return this.http.get<Parameter>(`${this.apiUrl}/${id}`);
  }

  getInactiveParameters(): Observable<Parameter[]> {
    return this.http.get<Parameter[]>(`${this.apiUrl}/inactivos`);
  }

  getActiveParameters(): Observable<Parameter[]> {
    return this.http.get<Parameter[]>(`${this.apiUrl}/activos`);
  }

  updateParameter(id: number, request: UpdateParameterRequest): Observable<Parameter> {
    // Add explicit headers to ensure JSON request and response
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Accept: 'application/json'
    });

    return this.http.put<Parameter>(`${this.apiUrl}/${id}`, request, { headers }).pipe(
      catchError(error => {
        // Handle content type mismatch errors
        if (error.status === 200 && error.error && typeof error.error === 'string') {
          // Server returned 200 but with text/plain content type
          this.logger.warn(
            'Server returned text/plain instead of application/json. Attempting to parse response.'
          );
          try {
            // Try to parse the text response as JSON
            const parsedResponse = JSON.parse(error.error);
            return [parsedResponse];
          } catch (parseError) {
            this.logger.error('Failed to parse server response as JSON', parseError);
            return throwError(
              () =>
                new Error(
                  'Server returned invalid response format. Expected JSON but received text/plain.'
                )
            );
          }
        }
        return throwError(() => error);
      })
    );
  }

  // Parameter Detail methods
  createParameterDetail(
    parameterId: number,
    detail: Partial<ParameterDetail>
  ): Observable<ParameterDetail> {
    // First, get the current parameter to add the detail to its array
    return this.getParameterById(parameterId).pipe(
      mergeMap(parameter => {
        const newDetail: ParameterDetail = {
          idParametroDetalle: Date.now(), // Generate temporary ID
          idPadre: null,
          parametroId: parameterId,
          vcNombre: detail.vcNombre || '',
          txDescripcion: detail.txDescripcion || '',
          vcCodigoInterno: detail.vcCodigoInterno || '',
          dCodigoIterno: detail.dCodigoIterno || 0,
          bEstado: detail.bEstado ?? true,
          rangoDesde: detail.rangoDesde || 0,
          rangoHasta: detail.rangoHasta || 0
        };

        // Add the new detail to the parameter's details array
        const updatedParameter: UpdateParameterRequest = {
          idParametro: parameter.idParametro,
          vcNombre: parameter.vcNombre,
          vcCodigoInterno: parameter.vcCodigoInterno,
          BEstado: parameter.bEstado
        };

        // Update the parameter (the backend should handle adding the detail)
        return this.updateParameter(parameterId, updatedParameter).pipe(
          map(() => newDetail) // Return the new detail
        );
      }),
      catchError(error => {
        this.logger.error('Error creating parameter detail:', error);
        return throwError(() => error);
      })
    );
  }

  updateParameterDetail(
    parameterId: number,
    detailId: number,
    detail: Partial<ParameterDetail>
  ): Observable<ParameterDetail> {
    // First, get the current parameter to find and update the specific detail
    return this.getParameterById(parameterId).pipe(
      mergeMap(parameter => {
        const existingDetailIndex = parameter.parametroDetalles?.findIndex(
          d => d.idParametroDetalle === detailId
        );

        if (existingDetailIndex === -1 || existingDetailIndex === undefined) {
          return throwError(() => new Error('Parameter detail not found'));
        }

        // Update the specific detail
        const updatedDetail: ParameterDetail = {
          ...parameter.parametroDetalles[existingDetailIndex],
          ...detail
        };

        // Update the parameter (the backend should handle updating the detail)
        const updateRequest: UpdateParameterRequest = {
          idParametro: parameter.idParametro,
          vcNombre: parameter.vcNombre,
          vcCodigoInterno: parameter.vcCodigoInterno,
          BEstado: parameter.bEstado
        };

        return this.updateParameter(parameterId, updateRequest).pipe(
          map(() => updatedDetail) // Return the updated detail
        );
      }),
      catchError(error => {
        this.logger.error('Error updating parameter detail:', error);
        return throwError(() => error);
      })
    );
  }
}
